<template>
  <main-input-dialog
    :is-visible.sync="computedIsVisible"
    :title-translation="$i18n.t('sidebar.masterplanStreets.copySituation')"
    agree-text="dialog.agreeCopy"
    disable-default-form-bottom-padding
    :inputs="inputs"
    @agree="toggleCopyConfirm"
  >
    <template v-for="input of inputs" #[`${input.name}.input`]="data">
      <v-col :key="input.name" cols="6" class="pt-8 pb-5">
        <data-input v-model="$data[input.name]" v-bind="data" />
      </v-col>
    </template>
    <template #info>
      <main-dialog
        v-if="confirmationLoaded"
        :is-visible.sync="confirmationVisible"
        :title="$i18n.t('sidebar.masterplanStreets.copySituation')"
        :body="$i18n.t('sidebar.masterplanStreets.copySituationConfirmation')"
        @agree="copy"
        :loading="loading"
        agree-text="dialog.agreeCopy"
      />
    </template>
  </main-input-dialog>
</template>
<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import MainDialog from '@/components/MainDialog';
import MainInputDialog from '@/components/MainInputDialog';

export default {
  name: 'MasterplanStreetsSituationCopyDialog',
  components: { DataInput, MainDialog, MainInputDialog },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  computed: {
    dataSourceName: get('admin/modulesMapping@masterplan_2_streets.masterplan_street_datasource.datasource'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    inputs() {
      return [
        {
          name: 'feature',
          translationPath: 'sidebar.masterplanStreets.copyFrom',
          subtitle: 'sidebar.masterplanStreets.copyFromSubtitle',
          dataType: { name: 'combobox' },
          itemText: 'desc',
          itemValue: 'id',
          items: this.features,
          loading: this.featuresLoading,
          readonly: this.featuresLoading,
          rules: [v => !!v || 'rules.required'],
        },
      ];
    },

    streetIdAttributeName: get(
      'admin/modulesMapping@masterplan_2_streets@masterplan_situation_datasource.settings.street_id_attribute'
    ),
    dataSources: get('layers/metadata'),
    lightingSituationDataSourceName: get(
      'admin/modulesMapping@masterplan_2_streets@masterplan_situation_datasource.datasource'
    ),
    lightingSituationDataSource() {
      return this.dataSources[this.lightingSituationDataSourceName] || undefined;
    },
  },
  data: () => ({
    featuresLoading: false,
    loading: false,
    feature: null,
    features: [],

    confirmationLoaded: false,
    confirmationVisible: false,
  }),
  methods: {
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    editMultipleDataSourceFeatures: call('layers/editMultipleDataSourceFeatures'),
    toggleCopyConfirm() {
      this.confirmationLoaded = true;
      this.confirmationVisible = true;
    },
    async copy() {
      this.loading = true;
      try {
        const rNewFeaturesTemplate = await this.getDataSourceFeatures({
          dataSource: this.lightingSituationDataSourceName,
          filters: {
            $EQUAL: {
              [this.lightingSituationDataSource?.attributes_use_datasource_qualified_names
                ? this.streetIdAttributeName
                : `${this.lightingSituationDataSourceName}.${this.streetIdAttributeName}`]: { value: this.feature.id },
            },
          },
        });
        const rFeaturesToDelete = await this.getDataSourceFeatures({
          dataSource: this.lightingSituationDataSourceName,
          filters: {
            $IN: {
              [this.lightingSituationDataSource?.attributes_use_datasource_qualified_names
                ? this.streetIdAttributeName
                : `${this.lightingSituationDataSourceName}.${this.streetIdAttributeName}`]: this.selectedItems,
            },
          },
          idsOnly: true,
        });
        const newFeaturesTemplate = rNewFeaturesTemplate.features.map(f =>
          this.$_filterObjectKeys(f.properties, {
            allowedKeys: this.lightingSituationDataSource.attributes_schema.attributes
              .filter(attribute => !attribute.read_only)
              .map(attribute => attribute.name),
            notAllowedKeys: [this.streetIdAttributeName],
          })
        );
        let newFeatures = this.selectedItems.reduce((features, selectedFeatureId) => {
          return [
            ...features,
            ...newFeaturesTemplate.map(nFeature => {
              return { ...nFeature, [this.streetIdAttributeName]: selectedFeatureId };
            }),
          ];
        }, []);
        const payload = {
          data_source_name: this.lightingSituationDataSourceName,
          insert: newFeatures,
          delete: { features_ids: rFeaturesToDelete },
        };
        await this.editMultipleDataSourceFeatures(payload);
      } finally {
        this.loading = false;
        this.confirmationVisible = false;
        this.computedIsVisible = false;
      }
    },
    async getFeaturesList() {
      this.featuresLoading = true;
      try {
        this.features =
          (
            await this.getDataSourceFeatures({
              dataSource: this.dataSourceName,
              idsDescsOnly: true,
            })
          )?.filter(feature => !this.selectedItems.includes(feature.id)) || [];
      } finally {
        this.featuresLoading = false;
      }
    },
  },
  watch: {
    computedIsVisible: {
      handler(nV) {
        if (nV) this.getFeaturesList();
      },
      immediate: true,
    },
  },
};
</script>
